import { Stack, Box, Divider } from "@mui/material";
import { Img } from "../Img";
import { forwardRef, useMemo } from "react";
import { XsText, XsTextMd } from "../Text";
import { useIntl } from "react-intl";
import { followRel, formatImg } from "helpers/format";
import ListingChip from "../chips/ListingChip";
import { COLORS } from "constants/layout";
import { DIRECT_PATHS } from "constants/routes";
import { BuyerType } from "types/buyer";
import StyledBuyer from "./Buyer.styles";
import RFQ from "./RFQ";
import useBreakpoints from "hooks/useBreakpoint";
import { SwiperSlide } from "swiper/react";
import { Pagination } from "swiper";
import BaseBuyerInfors from "Components/infos/BaseBuyerInfors";
import { Link } from "Components/link";
import CustomSwiper from "Components/custom-swiper";
import CrownTooltip from "Components/tooltip/CrownTooltip";
import VerificationStatus from "Components/tooltip/VerificationStatus";
import TimeDiff from "Components/TimeDiff";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

// Initialize dayjs plugins
dayjs.extend(relativeTime);

type Props = {
  company?: BuyerType;
  fullLink?: boolean;
  isShowOneRfq?: boolean;
  isNotShowRfq?: boolean;
};

const Buyer = forwardRef(
  ({ company, fullLink, isShowOneRfq, isNotShowRfq }: Props, ref: any) => {
    const intl = useIntl();
    const { isMobile } = useBreakpoints();
    const NOW = dayjs();

    const {
      slug,
      num_posted_rfq = 0,
      business_types,
      is_seen,
      activity_tags = [],
      no_follow = 0,
      import_countries,
      categories = [],
      rfqs = [],
      gallery_preview = [],
      payment_term,
      trade_term,
      num_employees,
      annual_revenue_amount,
      year_established,
      destination_port,
      is_featured,
      is_biz_verified,
      is_verified_work_email,
      is_verified_phone,
      is_verified_account_email,
      last_updated,
      // country_code,
      // country,
    } = company || {};

    const formattedRfqs = useMemo(
      () => rfqs.slice(0, gallery_preview.length > 0 ? 1 : 2),
      [rfqs, gallery_preview]
    );

    const infors = useMemo(
      () =>
        [
          { label: "business_types", value: business_types },
          { label: "import_countries", value: import_countries },
          { label: "number_of_employees", value: num_employees },
          { label: "annual_revenue_amount", value: annual_revenue_amount },
          { label: "year_established", value: year_established },
          { label: "prefer_payment_term", value: payment_term },
          { label: "prefer_trade_term", value: trade_term },
          { label: "prefer_desctination_term", value: destination_port },
        ].filter((info) => info.value),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [company]
    );

    const BuyerCardHeader = () => {
      return (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            width: "100%",
            overflow: "hidden",
          }}
        >
          {/* Left Section */}
          <Box sx={{ flexShrink: 0 }}>
            {Boolean(is_featured) && (
              <CrownTooltip
                title={intl.formatMessage({
                  id: "this_is_a_premium_sourcing_request",
                })}
                isFull
              />
            )}
          </Box>

          {/* Middle Section - Verification */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "center",
              minWidth: 0,
            }}
          >
            <VerificationStatus
              is_featured={is_featured}
              data={{
                is_biz_verified: is_biz_verified,
                is_verified_work_email: is_verified_work_email,
                is_verified_phone: is_verified_phone,
                is_verified_identity: is_verified_account_email,
              }}
              entity="buyer"
            />
          </Box>

          {/* Right Section */}
          <Box sx={{ flexShrink: 0 }}>
            {/* Show last active time */}
            <Stack direction="row" gap={0.5} alignItems="center">
              <XsText color={COLORS.gray[500]}>
                <TimeDiff
                  endTime={NOW.format()}
                  startTime={dayjs(last_updated).format()}
                />
              </XsText>
            </Stack>
          </Box>
        </Stack>
      );
    };

    return (
      <StyledBuyer
        ref={ref}
        className={`${is_seen ? "is-seen-box" : ""}  ${
          is_featured === 1 ? "gradient-border" : ""
        }`}
        marginTop={{ md: "12px !important" }}
        sx={{
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          overflow: "hidden",
          boxSizing: "border-box",
        }}
      >
        <Stack
          className={
            isNotShowRfq
              ? "info-box-3"
              : !isShowOneRfq
              ? "info-box"
              : "info-box-2"
          }
          spacing={1}
          sx={{
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            overflow: "hidden",
          }}
        >
          <BuyerCardHeader />
          <Divider />

          <Box sx={{ p: 1, pt: 0 }}>
            <Stack
              direction={isMobile ? "column" : "row"}
              justifyContent="space-between"
              alignItems={isMobile ? "flex-start" : "flex-start"}
              gap={1}
              sx={{
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <BaseBuyerInfors
                  {...{ company, fullLink }}
                  hideVerifyStatus
                  hideLastActive
                  countryPosition="rightOfStar"
                />
              </Box>
            </Stack>
            {num_posted_rfq > 0 && (
              <Box sx={{ flexShrink: 0 }}>
                <XsTextMd
                  className="status-label green-label"
                  sx={{
                    backgroundColor: `${COLORS.gray[100]}!important`,
                  }}
                >
                  {intl.formatMessage(
                    {
                      id: `qlt_buying_lead${
                        num_posted_rfq > 1 ? "s" : ""
                      }`,
                    },
                    { quantity: num_posted_rfq }
                  )}
                </XsTextMd>
              </Box>
            )}
            {/* Text infos stack */}
            <Stack
              spacing={1}
              sx={{ width: "100%", maxWidth: "100%", minWidth: 0, mt: 1 }}
            >
              {/* Buyer activity tags */}
              <ListingChip tags={activity_tags} />

              {categories.length > 0 && (
                <XsText
                  color={COLORS.green[600]}
                  className="categories-text cutoff-text twoline"
                  component="h3"
                >
                  {intl.formatMessage({ id: "buyer_of" })}:{" "}
                  {categories.map((category, i) => (
                    <Link
                      to={DIRECT_PATHS.categoryBuyer(
                        category.name,
                        undefined,
                        fullLink
                      )}
                      key={i}
                    >
                      {category.category_name}
                      {i === categories.length - 1 ? "" : ", "}
                    </Link>
                  ))}
                </XsText>
              )}

              <Stack
                direction="row"
                flexWrap="wrap"
                gap={1}
                sx={{ width: "100%", maxWidth: "100%", minWidth: 0 }}
              >
                {infors.map(({ value, label }, i) => (
                  <XsText className="info-text ellipsis-text" key={i}>
                    {intl.formatMessage({ id: label }) + ": "}{" "}
                    <span>{value}</span>
                  </XsText>
                ))}
              </Stack>
            </Stack>

            {isMobile && gallery_preview.length > 0 && (
              <div className="scroll-box medias">
                {gallery_preview.map((media, i) => (
                  <Img src={formatImg(media, 3)} alt="media" key={i} />
                ))}
              </div>
            )}
          </Box>
        </Stack>

        {/* RFQ list and gallery */}
        {!isMobile && !isNotShowRfq && (
          <Stack
            direction="row"
            gap={1}
            className={!isShowOneRfq ? "right-box" : "right-box-2"}
          >
            {formattedRfqs.length > 0 ? (
              !isShowOneRfq ? (
                formattedRfqs.map((rfq, i) => <RFQ rfq={rfq} key={i} />)
              ) : (
                formattedRfqs
                  .slice(0, 1)
                  .map((rfq, i) => <RFQ rfq={rfq} key={i} />)
              )
            ) : (
              <div className="rfq" />
            )}

            {!isShowOneRfq && gallery_preview.length > 0 && (
              <Link
                to={`/${slug}`}
                rel={followRel(no_follow)}
                className="media"
              >
                <CustomSwiper
                  className="medias"
                  modules={[Pagination]}
                  pagination={{
                    clickable: true,
                  }}
                >
                  {gallery_preview.map((media, i) => (
                    <SwiperSlide key={i}>
                      <Img
                        src={formatImg(media, 3)}
                        alt="media"
                        height="100%"
                        width="100%"
                        style={{ objectFit: "cover" }}
                      />
                    </SwiperSlide>
                  ))}
                </CustomSwiper>
              </Link>
            )}
          </Stack>
        )}
      </StyledBuyer>
    );
  }
);

export default Buyer;
